import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Form, Card, CardBody, Label, Input, FormFeedback, Row, Col } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { addUser, updateUserOperator, fetchAllUsers } from '../../slices/users/thunk';
import { getOperatorDropdown } from '../../helpers/apis';

const CreateUserModal = ({ show, toggle, userToEdit }) => {
    const dispatch = useDispatch();
    const [operators, setOperators] = useState([]);
    const [loadingOperators, setLoadingOperators] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const isEditing = !!userToEdit;

    useEffect(() => {
        if (show) {
            fetchOperators();
        }
    }, [show]);

    const fetchOperators = async () => {
        setLoadingOperators(true);
        try {
            const response = await getOperatorDropdown();
            if (response.success || response.status === 1) {
                setOperators(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching operators dropdown:", error);
        } finally {
            setLoadingOperators(false);
        }
    };

    const generatePassword = () => {
        let password = "";
        for (let i = 0; i < 6; i++) {
            password += Math.floor(Math.random() * 10);
        }
        validation.setFieldValue("password", password);
        validation.setFieldValue("confirmPassword", password);
        // Automatically show passwords when generated so user can see what was created
        setShowPassword(true);
        setShowConfirmPassword(true);
    };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            password: '',
            confirmPassword: '',
            operatorId: userToEdit?.createdBy?._id || userToEdit?.createdBy || '',
            gameAccess: {
                lt: userToEdit?.gameAccess?.lt !== undefined ? userToEdit.gameAccess.lt : true,
                lt1: userToEdit?.gameAccess?.lt1 !== undefined ? userToEdit.gameAccess.lt1 : true,
                ro: userToEdit?.gameAccess?.ro !== undefined ? userToEdit.gameAccess.ro : true,
                so: userToEdit?.gameAccess?.so !== undefined ? userToEdit.gameAccess.so : true,
            }
        },
        validationSchema: Yup.object({
            password: isEditing ? Yup.string() : Yup.string().required("Please Enter Password").min(6, "Password must be at least 6 characters"),
            confirmPassword: isEditing ? Yup.string() : Yup.string()
                .required("Please Confirm Password")
                .oneOf([Yup.ref('password'), null], "Passwords must match"),
            operatorId: Yup.string().required("Please Select Operator")
        }),
        onSubmit: async (values, { resetForm }) => {
            let result;
            if (isEditing) {
                result = await dispatch(updateUserOperator(userToEdit._id, { 
                    operatorId: values.operatorId,
                    gameAccess: values.gameAccess 
                }));
            } else {
                const { confirmPassword, ...submitValues } = values;
                result = await dispatch(addUser(submitValues));
            }

            if (result.success) {
                dispatch(fetchAllUsers());
                toggle();
                resetForm();
                // Reset visibility state
                setShowPassword(false);
                setShowConfirmPassword(false);
            }
        }
    });

    return (
        <Modal isOpen={show} toggle={toggle}>
            <ModalHeader toggle={toggle} tag="h4">
                {isEditing ? "Edit User Details" : "Create New User"}
            </ModalHeader>
            <ModalBody>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                    }}
                >
                    <Row>
                        <Col className="col-12">
                            {isEditing && (
                                <div className="mb-3 text-center">
                                    <div className="p-3 bg-light rounded mb-3">
                                        <h6 className="text-muted mb-1">User Account</h6>
                                        <h5 className="text-primary mb-0">{userToEdit?.acnumber}</h5>
                                    </div>
                                    <p className="text-muted small">Update the operator assigned and game access settings for this user.</p>
                                </div>
                            )}

                            <div className="mb-3">
                                <Label className="form-label">Operator</Label>
                                <Input
                                    type="select"
                                    name="operatorId"
                                    className="form-select"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.operatorId || ""}
                                    invalid={
                                        validation.touched.operatorId && validation.errors.operatorId ? true : false
                                    }
                                >
                                    <option value="">Select Operator</option>
                                    {loadingOperators ? (
                                        <option disabled>Loading...</option>
                                    ) : (
                                        operators.map((op) => (
                                            <option key={op._id} value={op._id}>
                                                {op.username}
                                            </option>
                                        ))
                                    )}
                                </Input>
                                {validation.touched.operatorId && validation.errors.operatorId ? (
                                    <FormFeedback type="invalid">{validation.errors.operatorId}</FormFeedback>
                                ) : null}
                            </div>

                            {/* Game Access Checkboxes */}
                            <div className="mb-3 p-3 bg-light rounded border">
                                <Label className="form-label fw-bold d-block mb-2 fs-12">Game Access</Label>
                                <Row className="g-2">
                                    <Col className="col-6">
                                        <div className="form-check">
                                            <Input
                                                type="checkbox"
                                                id="gameAccessLt"
                                                className="form-check-input"
                                                checked={validation.values.gameAccess.lt}
                                                onChange={(e) => {
                                                    validation.setFieldValue("gameAccess.lt", e.target.checked);
                                                }}
                                            />
                                            <Label className="form-check-label fw-semibold fs-12" htmlFor="gameAccessLt">
                                                LT (Lucky Target)
                                            </Label>
                                        </div>
                                    </Col>
                                    <Col className="col-6">
                                        <div className="form-check">
                                            <Input
                                                type="checkbox"
                                                id="gameAccessLt1"
                                                className="form-check-input"
                                                checked={validation.values.gameAccess.lt1}
                                                onChange={(e) => {
                                                    validation.setFieldValue("gameAccess.lt1", e.target.checked);
                                                }}
                                            />
                                            <Label className="form-check-label fw-semibold fs-12" htmlFor="gameAccessLt1">
                                                LT1 (Lucky Target 1)
                                            </Label>
                                        </div>
                                    </Col>
                                    <Col className="col-6">
                                        <div className="form-check">
                                            <Input
                                                type="checkbox"
                                                id="gameAccessRo"
                                                className="form-check-input"
                                                checked={validation.values.gameAccess.ro}
                                                onChange={(e) => {
                                                    validation.setFieldValue("gameAccess.ro", e.target.checked);
                                                }}
                                            />
                                            <Label className="form-check-label fw-semibold fs-12" htmlFor="gameAccessRo">
                                                RO (Roulette)
                                            </Label>
                                        </div>
                                    </Col>
                                    <Col className="col-6">
                                        <div className="form-check">
                                            <Input
                                                type="checkbox"
                                                id="gameAccessSo"
                                                className="form-check-input"
                                                checked={validation.values.gameAccess.so}
                                                onChange={(e) => {
                                                    validation.setFieldValue("gameAccess.so", e.target.checked);
                                                }}
                                            />
                                            <Label className="form-check-label fw-semibold fs-12" htmlFor="gameAccessSo">
                                                SO (Sorat)
                                            </Label>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {!isEditing && (
                                <>
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <Label className="form-label mb-0">Password</Label>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-info"
                                                onClick={generatePassword}
                                            >
                                                Generate Password
                                            </button>
                                        </div>
                                        <div className="position-relative auth-pass-inputgroup mb-3">
                                            <Input
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Enter Password"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.password || ""}
                                                invalid={
                                                    validation.touched.password && validation.errors.password ? true : false
                                                }
                                                className="form-control pe-5"
                                            />
                                            <button
                                                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{ zIndex: 10 }}
                                            >
                                                <i className={showPassword ? "ri-eye-off-fill" : "ri-eye-fill"}></i>
                                            </button>
                                            {validation.touched.password && validation.errors.password ? (
                                                <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <Label className="form-label">Confirm Password</Label>
                                        <div className="position-relative auth-pass-inputgroup mb-3">
                                            <Input
                                                name="confirmPassword"
                                                type={showConfirmPassword ? "text" : "password"}
                                                placeholder="Confirm Password"
                                                onChange={validation.handleChange}
                                                onBlur={validation.handleBlur}
                                                value={validation.values.confirmPassword || ""}
                                                invalid={
                                                    validation.touched.confirmPassword && validation.errors.confirmPassword ? true : false
                                                }
                                                className="form-control pe-5"
                                            />
                                            <button
                                                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                style={{ zIndex: 10 }}
                                            >
                                                <i className={showConfirmPassword ? "ri-eye-off-fill" : "ri-eye-fill"}></i>
                                            </button>
                                            {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                                                <FormFeedback type="invalid">{validation.errors.confirmPassword}</FormFeedback>
                                            ) : null}
                                        </div>
                                    </div>
                                </>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="text-end">
                                <button
                                    type="submit"
                                    className="btn btn-success save-user"
                                >
                                    {isEditing ? "Update User" : "Create User"}
                                </button>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default CreateUserModal;
